import { Integration } from '@dadohr/shared/domainTypes/integrations';
import React, { useContext, useMemo } from 'react';
import { useAdpWorkforceNowConfig } from '../../features/Org/pages/Integrations/integrations/adp/service';
import { useAsanaConfig } from '../../features/Org/pages/Integrations/integrations/asana/service';
import { useAtlassianConfig } from '../../features/Org/pages/Integrations/integrations/atlassian/service';
import { useBambooHrConfig } from '../../features/Org/pages/Integrations/integrations/bamboohr/service';
import { useCalendlyConfig } from '../../features/Org/pages/Integrations/integrations/calendly/service';
import { useCheckrConnectionStatus } from '../../features/Org/pages/Integrations/integrations/checkr/service';
import { useDocuSignConfig } from '../../features/Org/pages/Integrations/integrations/docusign/service';
import { useGoogleSheetsConfig } from '../../features/Org/pages/Integrations/integrations/googleSheets/service';
import { useLessonlyConfig } from '../../features/Org/pages/Integrations/integrations/lessonly/service';
import { useDoceboConfig } from '../../features/Org/pages/Integrations/integrations/docebo/service';
import { useMicrosoftConfig } from '../../features/Org/pages/Integrations/integrations/microsoft/service';
import { useOktaApiConfig } from '../../features/Org/pages/Integrations/integrations/okta_api/service';
import { usePersonioConfig } from '../../features/Org/pages/Integrations/integrations/personio/service';
import { useSalesforceConfig } from '../../features/Org/pages/Integrations/integrations/salesforce/service';
import { useSlackConfig } from '../../features/Org/pages/Integrations/integrations/slack/service';
import { useSwagUpConfig } from '../../features/Org/pages/Integrations/integrations/swagUp/service';
import { useCurrentUser } from '../currentUser';
import { useFeatureEnabled } from '../features';

export interface IIntegrationsContext {
  active: Set<Integration>;
}

export const IntegrationsContext = React.createContext<IIntegrationsContext>({
  active: new Set()
});

export const IntegrationsContextProvider: React.FC = ({ children }) => {
  const { id, org } = useCurrentUser();
  // This will get unwieldy quickly once we have more integrations
  // We can just change how we obtain this knowledge in this centralized place later, ideally with one call and one state update
  const [asana] = useAsanaConfig(org.id, id);
  const [atlassian] = useAtlassianConfig(org.id, id);
  const [calendly] = useCalendlyConfig(org.id, id);
  const [googleSheets] = useGoogleSheetsConfig(org.id, id);
  const [lessonly] = useLessonlyConfig(org.id, id);
  const [docebo] = useDoceboConfig(org.id, id);
  const [bambooHr] = useBambooHrConfig(org.id, id);
  const [slack] = useSlackConfig(org.id, id);
  const [swagUp] = useSwagUpConfig(org.id, id);
  const [microsoft] = useMicrosoftConfig(org.id, id);
  const [oktaApi] = useOktaApiConfig(org.id, id);
  const [docuSign] = useDocuSignConfig(org.id, id);
  const [adpWorkforceNow] = useAdpWorkforceNowConfig(org.id, id);
  const [personio] = usePersonioConfig(org.id, id);
  const [salesforce] = useSalesforceConfig(org.id, id);
  const isCheckrFeatureEnabled = useFeatureEnabled('CHECKR_V1');
  const [checkr] = useCheckrConnectionStatus(org.id);

  // TODO this is causing too many unnecessay re-renders.
  // Only update once

  const value = useMemo(() => {
    const active: Set<Integration> = new Set();
    if (asana && asana.data.active) {
      active.add(Integration.ASANA);
    }
    if (atlassian && atlassian.data.active) {
      active.add(Integration.ATLASSIAN);
    }
    if (bambooHr && bambooHr.data.active) {
      active.add(Integration.BAMBOOHR);
    }
    if (calendly && calendly.data.active) {
      active.add(Integration.CALENDLY);
    }
    if (googleSheets && googleSheets.data.active) {
      active.add(Integration.GOOGLE_SHEETS);
    }
    if (lessonly && lessonly.data.active) {
      active.add(Integration.LESSONLY);
    }
    if (docebo && docebo.data.active) {
      active.add(Integration.DOCEBO);
    }
    if (slack && slack.data.active) {
      active.add(Integration.SLACK);
    }
    if (swagUp && swagUp.data.active) {
      active.add(Integration.SWAG_UP);
    }
    if (microsoft && microsoft.data.active) {
      active.add(Integration.MICROSOFT);
    }
    if (oktaApi && oktaApi.data.active) {
      active.add(Integration.OKTA_API);
    }

    if (docuSign && docuSign.data.active) {
      active.add(Integration.DOCUSIGN);
    }
    if (adpWorkforceNow && adpWorkforceNow.data.active) {
      active.add(Integration.ADP_WORKFORCE_NOW);
    }
    if (personio && personio.data.active) {
      active.add(Integration.PERSONIO);
    }
    if (salesforce && salesforce.data.active) {
      active.add(Integration.SALESFORCE);
    }
    if (isCheckrFeatureEnabled && checkr && checkr.connected) {
      active.add(Integration.CHECKR);
    }
    return { active };
  }, [
    asana,
    atlassian,
    bambooHr,
    calendly,
    googleSheets,
    lessonly,
    docebo,
    slack,
    swagUp,
    microsoft,
    oktaApi,
    docuSign,
    adpWorkforceNow,
    personio,
    salesforce,
    checkr,
    isCheckrFeatureEnabled
  ]);

  return (
    <IntegrationsContext.Provider value={value}>
      {children}
    </IntegrationsContext.Provider>
  );
};

export const useIntegrationsContext = () => useContext(IntegrationsContext);
